import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Injectable, Injector } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ToastLife } from '../../../common/enums/toast-life.enum';
import { LogFactory } from '../../../common/services/logger/log-factory';

// User-facing copy — deliberately generic. We NEVER surface a raw
// `name: message` (e.g. "TypeError: Cannot read properties of undefined") to the
// admin user; the technical detail goes to the logger instead.
const GENERIC_ERROR_SUMMARY = 'Something went wrong';
const GENERIC_ERROR_DETAIL = 'An unexpected error occurred. Please try again.';

// Chunk/dynamic-import failures usually mean the admin is on a stale build after
// a deploy (old lazy chunk 404s). Offer a reload instead of a scary error.
const CHUNK_RELOAD_HEADER = 'Update available';
const CHUNK_RELOAD_MESSAGE =
  'A new version of the admin is available. Reload to continue?';
const CHUNK_RELOAD_ACCEPT = 'Reload';
const CHUNK_RELOAD_REJECT = 'Not now';

// Collapse a burst of identical toasts (the same error firing repeatedly) into
// one within this window.
const DEDUPE_WINDOW_MS = 4000;

/** Patterns that indicate a failed lazy-chunk / dynamic ESM import. */
const CHUNK_ERROR_PATTERNS = [
  /loading chunk [\d]+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
];

/**
 * Last-resort handler for everything that is NOT an HTTP response (uncaught
 * exceptions in components/templates, errors thrown inside subscribe callbacks,
 * unhandled promise rejections, failed lazy-chunk loads). HTTP errors are owned
 * by the HTTP error interceptor and skipped here.
 *
 * UX contract: the admin only ever sees a friendly, generic message — never raw
 * technical text. Full detail is logged. Identical toasts are de-duped, and
 * stale-build chunk errors become a reload prompt rather than an error. Mirrors
 * the user-app handler; neither surface sends errors off-device (ADR-0088).
 */
@Injectable({ providedIn: 'root' })
export class GlobalErrorHandlerService implements ErrorHandler {
  private readonly injector = inject(Injector);
  private readonly logger = inject(LogFactory).createLogger(
    GlobalErrorHandlerService,
  );

  /** key -> last-shown timestamp, for toast de-duplication. */
  private readonly recentToasts = new Map<string, number>();
  /** Guards against stacking multiple reload prompts. */
  private chunkReloadPrompted = false;

  handleError(error: unknown): void {
    // HTTP errors are surfaced (with friendly copy) by the HTTP error
    // interceptor. Don't double-handle them here.
    if (error instanceof HttpErrorResponse) {
      return;
    }

    // Always log the real error — this is the one place uncaught bugs surface.
    // Error props (name/message/stack) are non-enumerable, so a raw Error
    // serializes to a useless `{}` — extract them explicitly so the log is
    // actually actionable.
    this.logger.error('Global Error Caught:', this.describe(error));

    // Stale-build chunk failure → offer a reload, not an error toast.
    if (this.isChunkLoadError(error)) {
      this.promptReload();
      return;
    }

    // Nothing actionable to show for a nullish throw — just log it.
    if (error == null) {
      return;
    }

    // Everything else: a single friendly, generic toast. No raw detail.
    this.showToastOnce();
  }

  /** Turn an error into something that logs readably (Error fields are
   *  non-enumerable, so the raw object prints as `{}`). */
  private describe(error: unknown): unknown {
    if (error instanceof Error) {
      return { name: error.name, message: error.message, stack: error.stack };
    }
    return error;
  }

  private isChunkLoadError(error: unknown): boolean {
    const name = (error as { name?: string })?.name ?? '';
    if (name === 'ChunkLoadError') return true;
    const message = (error as { message?: string })?.message ?? '';
    return CHUNK_ERROR_PATTERNS.some((re) => re.test(message));
  }

  private promptReload(): void {
    if (this.chunkReloadPrompted) return;
    this.chunkReloadPrompted = true;
    try {
      this.injector.get(ConfirmationService).confirm({
        header: CHUNK_RELOAD_HEADER,
        message: CHUNK_RELOAD_MESSAGE,
        acceptLabel: CHUNK_RELOAD_ACCEPT,
        rejectLabel: CHUNK_RELOAD_REJECT,
        accept: () => window.location.reload(),
        reject: () => {
          // Allow another prompt later if they dismiss this one.
          this.chunkReloadPrompted = false;
        },
      });
    } catch (err) {
      this.chunkReloadPrompted = false;
      this.logger.error('Failed to present reload prompt.', err);
    }
  }

  /** Show a toast unless an identical one fired within DEDUPE_WINDOW_MS. */
  private showToastOnce(): void {
    const key = `${GENERIC_ERROR_SUMMARY}|${GENERIC_ERROR_DETAIL}`;
    const now = Date.now();
    const last = this.recentToasts.get(key);
    if (last !== undefined && now - last < DEDUPE_WINDOW_MS) {
      this.logger.debug('Duplicate error toast suppressed.');
      return;
    }
    this.recentToasts.set(key, now);
    this.injector.get(MessageService).add({
      severity: 'error',
      summary: GENERIC_ERROR_SUMMARY,
      detail: GENERIC_ERROR_DETAIL,
      life: ToastLife.MEDIUM,
    });
  }
}
